import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import Grid from "@mui/material/Grid2";
import { Button, Checkbox, FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import AgreementConditionTypeSelect from "view/agreementconditions/components/select/AgreementConditionTypeSelect";
import { Box, List, ListItem, ListItemText, ListSubheader, Typography } from "@mui/material";
import { AgreementConditionType, LocationType, CallType, AgreementTypeEnum } from "__generated__/graphql";
import { ReactNode } from "react";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import CallTypeSelect from "components/selects/CallTypeSelect";
import ProductSelect from "view/products/components/select/ProductSelect";
import FormField from "components/cck/FormField";
import ShipSelect from "view/ships/components/selects/ShipSelect";
import WaterLevelSelect from "view/waterlevels/components/selects/WaterLevelSelect";
import NumberInput from "components/cck/NumberInput";
import useRequest from "components/hooks/useRequest";
import { WaterLevel } from "view/waterlevels/types/waterlevel";
import { ChecklistOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { ReeferCheckType } from "__generated__/enums.ts";
import ReeferCheckTypeSelect from "view/calculationmethods/components/select/ReeferCheckTypeSelect.tsx";

type AgreementConditionFormProps = {
	index: number;
}

type FormField = {
	id: string;
	render: () => ReactNode;
}

type FormValues = {
	agreements: {
		type?: AgreementTypeEnum;
		conditions: {
			id?: number;
			type?: AgreementConditionType;
			location?: {
				id: number;
				description: string;
				type: LocationType;
				locationGroups: {
					id: number;
					description: string;
					type: LocationType;
				}[];
			};
			locationActionType?: CallType;
			fromLocation?: {
				id: number;
				description: string;
				type: LocationType;
				locationGroups: {
					id: number;
					description: string;
					type: LocationType;
				}[];
			};
			toLocation?: {
				id: number;
				description: string;
				type: LocationType;
				locationGroups: {
					id: number;
					description: string;
					type: LocationType;
				}[];
			};
			product?: {
				id: number;
				code?: string;
				name: string;
			};
			empty?: boolean;
			ship?: {
				id: number;
				name: string;
			};
			waterLevelLocation?: number;
			waterLevelMin?: number;
			waterLevelMax?: number;
			reeferCheckType?: ReeferCheckType;
		}[];
	}[];
}

const AgreementConditionForm = ({ index }: AgreementConditionFormProps) => {
	const { control } = useFormContext<FormValues>();

	const { fields, append, remove } = useFieldArray({
		control: control,
		keyName: "generatedId",
		name: `agreements.${index}.conditions`,
	});

	const defaultCondition = {
		id: undefined,
		type: undefined,
	};

	return (
		<Grid container size={12}>
			<Grid size={12} marginTop={2}>
				<List
					dense
					subheader={
						<ListSubheader
							component="div"
							sx={{ padding: 0 }}
							disableSticky>
							<Box sx={{  display: "flex", alignItems: "center", justifyContent: "space-between" }}>
								<Box sx={{  display: "flex", alignItems: "center" }}>
									<ChecklistOutlined fontSize="small" />
									<Typography color="textPrimary"
										fontSize={13}
										fontWeight="medium"
										marginLeft={1}>
										{intl.formatMessage({ id: "AGREEMENT_CONDITIONS" })}
									</Typography>
								</Box>
								<Button
									onClick={() => append({ ...defaultCondition })}
									icon={<AddIcon />}
									size="small"
									variant="outlined" />
							</Box>
						</ListSubheader>
					}
				>
					{fields.map((condition, conditionIndex) => (
						<ListItem
							key={condition.id || condition.generatedId}
							disablePadding
							secondaryAction={
								<Button
									onClick={() => remove(conditionIndex)}
									icon={<DeleteIcon />}
									size="small"
									variant="text" />
							}>
							<Grid container
								spacing={2}
								size={12}
								marginTop={2}>
								<Grid size={{ md: 2, xs: 12 }}>
									<ListItemText
										sx={{ margin: 0 }}
										primary={
											<Typography component="span"
												fontSize={13}
												fontStyle="normal"
												fontWeight="medium">
												{condition.id != null ? (
													`${intl.formatMessage({ id: `AGREEMENT_CONDITION_TYPE_${condition.type}` })} ${intl.formatMessage({ id: "AGREEMENT_CONDITION" })}`
												) : (
													`${intl.formatMessage({ id: "AGREEMENT_CONDITION" })} ${conditionIndex + 1}`
												)}
											</Typography>
										} />
								</Grid>
								<Form agreementIndex={index} conditionIndex={conditionIndex} />
							</Grid>
						</ListItem>
					))}
				</List>
			</Grid>
		</Grid>
	);
};

const Form = ({ agreementIndex, conditionIndex }: { agreementIndex: number; conditionIndex: number; }) => {
	const { control, formState: { errors } } = useFormContext<FormValues>();
	const [ measuringPoints ] = useRequest<WaterLevel[]>({ defaultResponse: [], url: "waterlevels/measuring-points" });

	const type = useWatch({ control, name: `agreements.${agreementIndex}.type` });
	const conditions = useWatch({ control, name: `agreements.${agreementIndex}.conditions` }) ?? [];
	const id = conditions?.[conditionIndex].id;
	const conditionType = conditions?.[conditionIndex].type;

	const isOptionDisabled = (index: number, option: AgreementConditionType) => {
		const conditionTypes = conditions?.filter((_, i) => i !== index).map(x => x.type);

		if (conditionTypes == null) {
			return false;
		}

		if (conditionTypes.includes(option)) {
			return true;
		}

		if (option === AgreementConditionType.Location) {
			return conditionTypes.includes(AgreementConditionType.Route);
		}

		if (option === AgreementConditionType.Route) {
			return conditionTypes.includes(AgreementConditionType.Location);
		}

		return false;
	};

	const AgreementConditionFormFields: FormField[] = [
		{
			id: "conditions.type",
			render: () => id == null && type && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.type`}>
						<FormGroup label={intl.formatMessage({ id: "TYPE" })} required>
							<Controller
								rules={{ required: true }}
								control={control}
								render={({ field }) => (
									<AgreementConditionTypeSelect
										{...field}
										agreementType={type}
										getOptionDisabled={(option => isOptionDisabled(conditionIndex, option))}
									/>
								)}
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.type`} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.location",
			render: () => conditionType === AgreementConditionType.Location && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.location`}>
						<FormGroup label={intl.formatMessage({ id: "LOCATION" })} overflow="hidden" required>
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.location`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<LocationSelect
										{...field}
										types={[
											LocationType.Terminal,
											LocationType.Port,
											LocationType.Area,
											LocationType.ExtractionMachine,
											LocationType.Buoy,
										]}
									/>
								)}
							/>
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.locationActionType",
			render: () => conditionType === AgreementConditionType.Location && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.locationActionType`}>
						<FormGroup label={intl.formatMessage({ id: "LOCATION_ACTION_TYPE" })} overflow="hidden" required>
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.locationActionType`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<CallTypeSelect
										{...field}
										additionalOptions={[ CallType.LoadingDischarging ]}
									/>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.fromLocation",
			render: () => conditionType === AgreementConditionType.Route && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.fromLocation`}>
						<FormGroup label={intl.formatMessage({ id: "ROUTE_FROM" })} overflow="hidden" required>
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.fromLocation`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<LocationSelect
										{...field}
										types={[
											LocationType.Terminal,
											LocationType.Port,
											LocationType.Area,
											LocationType.ExtractionMachine,
											LocationType.Buoy,
										]} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.toLocation",
			render: () => conditionType === AgreementConditionType.Route && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.toLocation`}>
						<FormGroup label={intl.formatMessage({ id: "ROUTE_TO" })} overflow="hidden" required>
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.toLocation`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<LocationSelect
										{...field}
										types={[
											LocationType.Terminal,
											LocationType.Port,
											LocationType.Area,
											LocationType.ExtractionMachine,
											LocationType.Buoy,
										]} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.product",
			render: () => conditionType === AgreementConditionType.Product && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.product`}>
						<FormGroup label={intl.formatMessage({ id: "PRODUCT" })} overflow="hidden" required>
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.product`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => <ProductSelect {...field} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.empty",
			render: () => conditionType === AgreementConditionType.ContainerFull && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.empty`}>
						<FormGroup label={intl.formatMessage({ id: "EMPTY" })} overflow="hidden">
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.empty`}
								control={control}
								render={({ field }) => (
									<div style={{ marginTop: 10 }}>
										<Checkbox {...field} value={undefined} checked={field.value || false} />
									</div>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.ship",
			render: () => conditionType === AgreementConditionType.Ship && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.ship`}>
						<FormGroup label={intl.formatMessage({ id: "SHIP" })} overflow="hidden" required>
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.ship`}
								rules={{ required: true }}
								control={control}
								render={({ field }) => <ShipSelect {...field} />} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.waterLevelLocation",
			render: () => conditionType === AgreementConditionType.WaterLevel && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.waterLevelLocation`}>
						<FormGroup label={intl.formatMessage({ id: "LOCATION" })} overflow="hidden" required>
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.waterLevelLocation`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<WaterLevelSelect
										value={measuringPoints?.find(m => m.id === field.value)}
										onChange={option => field.onChange(option?.id)} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.waterLevelMin",
			render: () => conditionType === AgreementConditionType.WaterLevel && (
				<Grid size={{ md: 2, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.waterLevelMin`}>
						<FormGroup label={intl.formatMessage({ id: "TIER_MINIMUM_WATER_LEVEL" })} overflow="hidden">
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.waterLevelMin`}
								control={control}
								render={({ field }) => (
									<NumberInput {...field} addonAfter="m" />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.waterLevelMax",
			render: () => conditionType === AgreementConditionType.WaterLevel && (
				<Grid size={{ md: 2, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.waterLevelMax`}>
						<FormGroup label={intl.formatMessage({ id: "TIER_MAXIMUM_WATER_LEVEL" })} overflow="hidden">
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.waterLevelMax`}
								control={control}
								render={({ field }) => (
									<NumberInput {...field} addonAfter="m" />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "conditions.reeferCheckType",
			render: () => conditionType === AgreementConditionType.ReeferCheckType && (
				<Grid size={{ md: 3, xs: 12 }}>
					<FormField errors={errors} name={`agreements.${agreementIndex}.conditions.${conditionIndex}.reeferCheckType`}>
						<FormGroup label={intl.formatMessage({ id: "REEFER_CHECK_TYPE" })} required={true}>
							<Controller
								name={`agreements.${agreementIndex}.conditions.${conditionIndex}.reeferCheckType`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<ReeferCheckTypeSelect
										{...field}
										types={[
											ReeferCheckType.Economy,
											ReeferCheckType.Premium,
											ReeferCheckType.Plugin,
										]}
									/>
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];


	return (
		<>
			{AgreementConditionFormFields.map(x => (
				x.render()
			))}
		</>
	);
};

export default AgreementConditionForm;
